import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { ColorPicker, useColor, ColorService, type IColor } from "react-color-palette";
import "react-color-palette/css";
import * as Dialog from "@radix-ui/react-dialog";
import Input from "@/components/ui/input";
import Select from "@/components/ui/select";
import FontSelect from "@/components/ui/font-select";
import ImageUploadField from "@/components/ui/image-upload-field";
import { useAuth } from "@/modules/auth";
import type { BrandKitUpdateRequest } from "@/modules/brand/types";
import BrandingSkeleton from "./branding-skeleton";
import { useBrandKits } from "@/modules/brand/hooks/use-brand-kits";
import { useBrands, useFonts, usePlan } from "@/modules/brand";
import { useOpenUpgradeModal } from "@/hooks/use-upgrade-modal";
import Button from "@/components/ui/button";
import Container from "@/components/ui/container";
import { useNavigationGuard } from "@/contexts/navigation-guard-context";
import { useOnboardingStore } from "@/modules/onboarding/stores/onboarding-store";
import { getTextColorForBackground } from "@/lib/utils";

// Track loaded font weights to avoid duplicate loading
const loadedFontWeights = new Map<string, Set<string>>();

function loadGoogleFont(fontName: string, weights: string[] = ["300", "400", "500", "600", "700", "800"]) {
	// Get or create the set of loaded weights for this font
	if (!loadedFontWeights.has(fontName)) {
		loadedFontWeights.set(fontName, new Set());
	}
	const loadedWeights = loadedFontWeights.get(fontName)!;

	// Filter out weights that are already loaded
	const newWeights = weights.filter((w) => !loadedWeights.has(w));
	if (newWeights.length === 0) return;

	// Mark new weights as loaded
	newWeights.forEach((w) => loadedWeights.add(w));

	// Get all weights to load (combine existing and new)
	const allWeights = Array.from(loadedWeights).sort();

	const linkId = `google-font-${fontName.replace(/\s+/g, "-").toLowerCase()}`;
	const existingLink = document.getElementById(linkId);

	const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@${allWeights.join(";")}&display=swap`;

	if (existingLink) {
		// Update existing link with all weights
		(existingLink as HTMLLinkElement).href = href;
	} else {
		// Create new link element
		const link = document.createElement("link");
		link.id = linkId;
		link.rel = "stylesheet";
		link.href = href;
		document.head.appendChild(link);
	}
}

const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

// Weight labels for display
const WEIGHT_LABELS: Record<string, string> = {
	"100": "Thin",
	"200": "Extra Light",
	"300": "Light",
	"400": "Regular",
	"500": "Medium",
	"600": "Semibold",
	"700": "Bold",
	"800": "Extra Bold",
	"900": "Black",
};

interface ColorPickerDropdownProps {
	color: IColor;
	onChange: (color: IColor) => void;
	onChangeComplete: (color: IColor) => void;
	label: string;
	required?: boolean;
	onValidationChange?: (error: string | undefined) => void;
	defaultColor?: string; // Default hex color when input is empty (e.g., "#000000")
	variant?: "light" | "dark";
}

// Helper function to strip # from hex color
const stripHash = (value: string): string => {
	return value.replace(/#/g, "");
};

// Helper function to ensure hex has # prefix
const ensureHashPrefix = (value: string): string => {
	return value.startsWith("#") ? value : `#${value}`;
};

const normalizeHex = (value: string | null | undefined, fallback: string): string => {
	const normalized = ColorService.convert("hex", value || fallback).hex;
	return normalized.toLowerCase();
};

function ColorPickerDropdown({ color, onChange, onChangeComplete, label, required = false, onValidationChange, defaultColor, variant = "light" }: ColorPickerDropdownProps) {
	const [isOpen, setIsOpen] = useState(false);
	const isDark = variant === "dark";
	// Store input value without # prefix since we display it as a prefix
	const [inputValue, setInputValue] = useState(stripHash(color.hex));
	const [error, setError] = useState<string | undefined>(undefined);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const initialColorRef = useRef(color.hex);

	useEffect(() => {
		// Store value without # prefix
		setInputValue(stripHash(color.hex));
		// Clear error when color is set externally (e.g., from server)
		if (color.hex && hexColorRegex.test(color.hex)) {
			setError(undefined);
		}
	}, [color.hex]);

	// Notify parent of validation changes
	useEffect(() => {
		onValidationChange?.(error);
	}, [error, onValidationChange]);

	useEffect(() => {
		if (isOpen) {
			initialColorRef.current = color.hex;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;

		function handleClickOutside(event: MouseEvent) {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
				// Trigger autosave when closing the picker if color changed
				if (initialColorRef.current !== color.hex) {
					onChangeComplete(color);
				}
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isOpen, color, onChangeComplete]);

	const validateHex = (value: string): string | undefined => {
		// Value is stored without #, so we need to add it for validation
		const valueWithHash = ensureHashPrefix(value);
		if (required && !value.trim()) {
			return "This field is required";
		}
		if (value.trim() && !hexColorRegex.test(valueWithHash)) {
			return "Please enter a valid hex color.";
		}
		return undefined;
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		// Strip all # characters from input
		const rawValue = e.target.value;
		const value = stripHash(rawValue);
		setInputValue(value);

		// Validate with # prefix added
		const validationError = validateHex(value);
		setError(validationError);

		// Convert to color if valid (add # prefix for conversion)
		const valueWithHash = ensureHashPrefix(value);
		if (hexColorRegex.test(valueWithHash)) {
			onChange(ColorService.convert("hex", valueWithHash));
		}
	};

	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pastedText = e.clipboardData.getData("text");
		// Strip all # characters from pasted content
		const value = stripHash(pastedText);
		setInputValue(value);

		// Validate with # prefix added
		const validationError = validateHex(value);
		setError(validationError);

		// Convert to color if valid (add # prefix for conversion)
		const valueWithHash = ensureHashPrefix(value);
		if (hexColorRegex.test(valueWithHash)) {
			onChange(ColorService.convert("hex", valueWithHash));
		}
	};

	const handleInputBlur = () => {
		// If input is empty and defaultColor is provided, set to default
		if (!inputValue.trim() && defaultColor) {
			const defaultColorObj = ColorService.convert("hex", defaultColor);
			setInputValue(stripHash(defaultColor));
			setError(undefined);
			onChange(defaultColorObj);
			onChangeComplete(defaultColorObj);
			return;
		}

		const validationError = validateHex(inputValue);
		setError(validationError);

		if (!validationError) {
			const valueWithHash = ensureHashPrefix(inputValue);
			if (hexColorRegex.test(valueWithHash) && valueWithHash !== initialColorRef.current) {
				onChangeComplete(ColorService.convert("hex", valueWithHash));
			}
		}
	};

	return (
		<div className="relative" ref={dropdownRef}>
			<div className="flex items-center gap-4">
				<button
					type="button"
					onClick={() => setIsOpen(!isOpen)}
					className={`w-12 h-12 rounded-xl shadow-sm border-2 transition-all duration-200 cursor-pointer shrink-0 hover:scale-105 hover:shadow-md ${
						error ? "border-red-500 hover:border-red-600" : isDark ? "border-gray-600 hover:border-gray-500" : "border-gray-200 hover:border-gray-300"
					}`}
					style={{ backgroundColor: color.hex }}
					aria-label={`Select ${label.toLowerCase()} color`}
				/>
				<div className="flex-1 min-w-0">
					<label className={`block font-medium text-sm mb-1 ${isDark ? "text-gray-200" : "text-gray-900"}`}>
						{label}
						{required && <span className="text-red-500"> *</span>}
					</label>
					<div className="flex items-center gap-2">
						<Input
							sizeVariant="default"
							value={inputValue}
							onChange={handleInputChange}
							onPaste={handlePaste}
							onBlur={handleInputBlur}
							className="font-mono w-28 text-sm"
							placeholder="000000"
							required={required}
							error={error}
							variant={variant}
							inputPrefix={<span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-400"}`}>#</span>}
						/>
					</div>
				</div>
			</div>
			{isOpen && (
				<div
					className={`absolute z-50 mt-3 p-2 rounded-xl shadow-xl border w-64 animate-in fade-in slide-in-from-top-2 duration-200 ${
						isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
					}`}
				>
					<ColorPicker color={color} onChange={onChange} hideInput hideAlpha />
				</div>
			)}
		</div>
	);
}

// Helper to convert data URL to File
function dataURLtoFile(dataUrl: string, baseFilename: string): File {
	const arr = dataUrl.split(",");
	const mime = arr[0].match(/:(.*?);/)?.[1] || "image/jpeg";
	const bstr = atob(arr[1]);
	let n = bstr.length;
	const u8arr = new Uint8Array(n);
	while (n--) {
		u8arr[n] = bstr.charCodeAt(n);
	}
	// Get correct extension based on MIME type
	const extMap: Record<string, string> = {
		"image/jpeg": ".jpg",
		"image/png": ".png",
		"image/gif": ".gif",
		"image/webp": ".webp",
	};
	const ext = extMap[mime] || ".jpg";
	const filename = baseFilename.replace(/\.[^/.]+$/, "") + ext;
	return new File([u8arr], filename, { type: mime });
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

// Interface for tracking original values
interface OriginalValues {
	primaryColor: string;
	accentColor: string;
	headingFont: string;
	headingWeight: string;
	bodyFont: string;
	bodyWeight: string;
	// Logo URLs from server
	primaryLogo: string | null;
	iconOnlyLogo: string | null;
	labelOnlyLogo: string | null;
	faviconLogo: string | null;
	invertedPrimaryLogo: string | null;
	invertedIconLogo: string | null;
	invertedLabelLogo: string | null;
}

// Interface for tracking pending image changes (data URLs to be uploaded)
interface PendingImageChanges {
	primaryLogo?: { dataUrl: string | null; action: "upload" | "delete" };
	iconOnlyLogo?: { dataUrl: string | null; action: "upload" | "delete" };
	labelOnlyLogo?: { dataUrl: string | null; action: "upload" | "delete" };
	faviconLogo?: { dataUrl: string | null; action: "upload" | "delete" };
	invertedPrimaryLogo?: { dataUrl: string | null; action: "upload" | "delete" };
	invertedIconLogo?: { dataUrl: string | null; action: "upload" | "delete" };
	invertedLabelLogo?: { dataUrl: string | null; action: "upload" | "delete" };
}

const BRANDING_PRESETS = [
	{
		id: "default",
		name: "Default",
		primaryColor: "#222222",
		accentColor: "#4b5563",
		headingFont: "Inter",
		headingWeight: "600",
		bodyFont: "Inter",
		bodyWeight: "400",
	},
	{
		id: "indigo",
		name: "Indigo",
		primaryColor: "#4f46e5",
		accentColor: "#d97706",
		headingFont: "Inter",
		headingWeight: "700",
		bodyFont: "Inter",
		bodyWeight: "400",
	},
	{
		id: "ocean",
		name: "Ocean",
		primaryColor: "#0891b2",
		accentColor: "#0284c7",
		headingFont: "Inter",
		headingWeight: "600",
		bodyFont: "Inter",
		bodyWeight: "400",
	},
	{
		id: "elegant",
		name: "Elegant",
		primaryColor: "#1f2933",
		accentColor: "#9333ea",
		headingFont: "Playfair Display",
		headingWeight: "700",
		bodyFont: "Inter",
		bodyWeight: "400",
	},
];

export default function BrandingPage() {
	const { currentWorkspaceId } = useAuth();
	const onboardingStore = useOnboardingStore();
	const { brands, selectedBrand } = useBrands();
	const { setHasUnsavedChanges } = useNavigationGuard();
	const { planDetails, getPlan } = usePlan();
	const openUpgradeModal = useOpenUpgradeModal();
	const {
		brandKit,
		viewBrandKit,
		updateBrandKit,
		updateIconWithLabel,
		deleteIconWithLabel,
		updateLogoIconOnly,
		deleteLogoIconOnly,
		updateLogoLabelOnly,
		deleteLogoLabelOnly,
		updateFavicon,
		deleteFavicon,
		updateLogoInvertedIconOnly,
		deleteLogoInvertedIconOnly,
		updateLogoInvertedIconWithLabel,
		deleteLogoInvertedIconWithLabel,
		updateLogoInvertedLabelOnly,
		deleteLogoInvertedLabelOnly,
	} = useBrandKits();

	// Font data from API
	const { fonts, fetchFonts, getWeightsForFont, getHeadingFontOptions, getBodyFontOptions } = useFonts();

	const [primaryColor, setPrimaryColor] = useColor("#000000");
	const [accentColor, setAccentColor] = useColor("#777777");
	const [logoImageMode] = useState<"light" | "dark">("light");
	const [previewDarkMode] = useState(false);

	// Confirmation dialog for preset selection
	const [showPresetConfirm, setShowPresetConfirm] = useState(false);
	const [pendingPreset, setPendingPreset] = useState<(typeof BRANDING_PRESETS)[0] | null>(null);

	// Track sidebar collapsed state for sticky footer positioning
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
		const saved = localStorage.getItem("sidebar-collapsed");
		return saved ? JSON.parse(saved) : false;
	});

	// Get current brand info
	const currentBrand = brands?.find((b) => b.id === currentWorkspaceId);
	const brandName = currentBrand?.name ?? "Your Brand";

	// Listen for sidebar collapse changes
	useEffect(() => {
		const handleSidebarChange = (e: CustomEvent<{ isCollapsed: boolean }>) => {
			setIsSidebarCollapsed(e.detail.isCollapsed);
		};

		window.addEventListener("sidebar-collapse-change", handleSidebarChange as EventListener);

		return () => {
			window.removeEventListener("sidebar-collapse-change", handleSidebarChange as EventListener);
		};
	}, []);

	// Font states
	const [headingFont, setHeadingFont] = useState("Inter");
	const [headingWeight, setHeadingWeight] = useState("700");
	const [bodyFont, setBodyFont] = useState("Inter");
	const [bodyWeight, setBodyWeight] = useState("400");

	// Logo states - now using URLs from brandKit
	const [primaryLogo, setPrimaryLogo] = useState<string | null>(null);
	const [iconOnlyLogo, setIconOnlyLogo] = useState<string | null>(null);
	const [labelOnlyLogo, setLabelOnlyLogo] = useState<string | null>(null);
	const [faviconLogo, setFaviconLogo] = useState<string | null>(null);
	const [invertedPrimaryLogo, setInvertedPrimaryLogo] = useState<string | null>(null);
	const [invertedIconLogo, setInvertedIconLogo] = useState<string | null>(null);
	const [invertedLabelLogo, setInvertedLabelLogo] = useState<string | null>(null);

	// Save status state
	const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
	const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Loading state
	const [isLoading, setIsLoading] = useState(true);

	// Track validation errors for color fields
	const [primaryColorError, setPrimaryColorError] = useState<string | undefined>(undefined);
	const [accentColorError, setAccentColorError] = useState<string | undefined>(undefined);

	// Track original values for dirty checking and discard functionality
	const [originalValues, setOriginalValues] = useState<OriginalValues>({
		primaryColor: "#000000",
		accentColor: "#777777",
		headingFont: "Inter",
		headingWeight: "700",
		bodyFont: "Inter",
		bodyWeight: "400",
		primaryLogo: null,
		iconOnlyLogo: null,
		labelOnlyLogo: null,
		faviconLogo: null,
		invertedPrimaryLogo: null,
		invertedIconLogo: null,
		invertedLabelLogo: null,
	});

	// Track pending image changes (data URLs to be uploaded on save)
	const [pendingImageChanges, setPendingImageChanges] = useState<PendingImageChanges>({});

	// Check if there are unsaved changes
	const hasUnsavedChanges = useMemo(() => {
		const hasPendingImages = Object.keys(pendingImageChanges).length > 0;
		return (
			primaryColor.hex.toLowerCase() !== originalValues.primaryColor ||
			accentColor.hex.toLowerCase() !== originalValues.accentColor ||
			headingFont !== originalValues.headingFont ||
			headingWeight !== originalValues.headingWeight ||
			bodyFont !== originalValues.bodyFont ||
			bodyWeight !== originalValues.bodyWeight ||
			hasPendingImages
		);
	}, [primaryColor.hex, accentColor.hex, headingFont, headingWeight, bodyFont, bodyWeight, originalValues, pendingImageChanges]);

	// Sync unsaved changes state with navigation guard
	useEffect(() => {
		setHasUnsavedChanges(hasUnsavedChanges);

		// Clean up when component unmounts
		return () => {
			setHasUnsavedChanges(false);
		};
	}, [hasUnsavedChanges, setHasUnsavedChanges]);

	// Fetch brand kit and fonts on mount
	useEffect(() => {
		if (currentWorkspaceId) {
			setIsLoading(true);
			Promise.all([viewBrandKit(currentWorkspaceId), fetchFonts(), getPlan(currentWorkspaceId)]).finally(() => {
				setIsLoading(false);
			});
		}
	}, [currentWorkspaceId, viewBrandKit, fetchFonts, getPlan]);

	// Get font options from the store (fonts dependency triggers re-computation when data loads)
	const headingFontOptions = useMemo(() => getHeadingFontOptions(), [fonts, getHeadingFontOptions]);
	const bodyFontOptions = useMemo(() => getBodyFontOptions(), [fonts, getBodyFontOptions]);

	// Dynamic weight options based on selected font
	const headingWeightOptions = useMemo(() => {
		const weights = getWeightsForFont(headingFont);
		return weights.map((w) => ({
			value: w,
			label: WEIGHT_LABELS[w] || w,
		}));
	}, [headingFont, fonts, getWeightsForFont]);

	const bodyWeightOptions = useMemo(() => {
		const weights = getWeightsForFont(bodyFont);
		return weights.map((w) => ({
			value: w,
			label: WEIGHT_LABELS[w] || w,
		}));
	}, [bodyFont, fonts, getWeightsForFont]);

	// Check if user has branding customization feature
	const canCustomizeBranding = planDetails?.plan?.features?.branding_customization !== false;

	// Check if current settings match a preset
	const selectedPresetId = useMemo(() => {
		const preset = BRANDING_PRESETS.find(
			(p) =>
				p.primaryColor.toLowerCase() === primaryColor.hex.toLowerCase() &&
				p.accentColor.toLowerCase() === accentColor.hex.toLowerCase() &&
				p.headingFont === headingFont &&
				p.headingWeight === headingWeight &&
				p.bodyFont === bodyFont &&
				p.bodyWeight === bodyWeight
		);
		return preset?.id ?? null;
	}, [primaryColor.hex, accentColor.hex, headingFont, headingWeight, bodyFont, bodyWeight]);

	// Check if user has custom branding (brand was previously modified and doesn't match any preset)
	const hasCustomBranding = useMemo(() => {
		if(selectedPresetId) return false;
		if (!brandKit?.updated_at || !brandKit?.created_at) return false;
		return brandKit.updated_at !== brandKit.created_at;
	}, [selectedPresetId, brandKit]);

	// Auto-reset weight when font changes and current weight is not available
	useEffect(() => {
		const availableWeights = getWeightsForFont(headingFont);
		if (availableWeights.length > 0 && !availableWeights.includes(headingWeight)) {
			// Prefer 700 (Bold) for headings, fallback to highest available weight
			const preferredWeight = availableWeights.includes("700") ? "700" : availableWeights[availableWeights.length - 1];
			setHeadingWeight(preferredWeight);
		}
	}, [headingFont, headingWeight, fonts, getWeightsForFont]);

	useEffect(() => {
		const availableWeights = getWeightsForFont(bodyFont);
		if (availableWeights.length > 0 && !availableWeights.includes(bodyWeight)) {
			// Prefer 400 (Regular) for body, fallback to first available weight
			const preferredWeight = availableWeights.includes("400") ? "400" : availableWeights[0];
			setBodyWeight(preferredWeight);
		}
	}, [bodyFont, bodyWeight, fonts, getWeightsForFont]);

	// Sync local state with brandKit when it loads
	useEffect(() => {
		if (brandKit) {
			const newPrimaryColor = normalizeHex(brandKit.color_primary_hex, "#000000");
			const newAccentColor = normalizeHex(brandKit.color_accent_hex, "#777777");
			const newHeadingFont = brandKit.font_heading_name || "Inter";
			const newHeadingWeight = brandKit.font_heading_weight || "700";
			const newBodyFont = brandKit.font_body_name || "Inter";
			const newBodyWeight = brandKit.font_body_weight || "400";

			// Update colors
			setPrimaryColor(ColorService.convert("hex", newPrimaryColor));
			setAccentColor(ColorService.convert("hex", newAccentColor));

			// Update fonts
			setHeadingFont(newHeadingFont);
			setHeadingWeight(newHeadingWeight);
			setBodyFont(newBodyFont);
			setBodyWeight(newBodyWeight);

			// Update logos
			setPrimaryLogo(brandKit.logo_icon_with_label_url);
			setIconOnlyLogo(brandKit.logo_icon_only_url);
			setLabelOnlyLogo(brandKit.logo_label_only_url);
			setFaviconLogo(brandKit.favicon_url);
			setInvertedPrimaryLogo(brandKit.logo_inverted_icon_with_label_url);
			setInvertedIconLogo(brandKit.logo_inverted_icon_only_url);
			setInvertedLabelLogo(brandKit.logo_inverted_label_only_url);

			// Store original values for dirty checking
			setOriginalValues({
				primaryColor: newPrimaryColor,
				accentColor: newAccentColor,
				headingFont: newHeadingFont,
				headingWeight: newHeadingWeight,
				bodyFont: newBodyFont,
				bodyWeight: newBodyWeight,
				primaryLogo: brandKit.logo_icon_with_label_url ?? null,
				iconOnlyLogo: brandKit.logo_icon_only_url ?? null,
				labelOnlyLogo: brandKit.logo_label_only_url ?? null,
				faviconLogo: brandKit.favicon_url ?? null,
				invertedPrimaryLogo: brandKit.logo_inverted_icon_with_label_url ?? null,
				invertedIconLogo: brandKit.logo_inverted_icon_only_url ?? null,
				invertedLabelLogo: brandKit.logo_inverted_label_only_url ?? null,
			});

			// Clear any pending image changes when brandKit updates from server
			setPendingImageChanges({});
		}
	}, [brandKit, setPrimaryColor, setAccentColor]);

	// Auto-complete customize_branding if brandKit has been modified
	useEffect(() => {
		if (brandKit && currentWorkspaceId && brandKit.updated_at && brandKit.updated_at !== brandKit.created_at) {
			const store = useOnboardingStore.getState();
			if (!store.completedSteps.includes("customize_branding")) {
				store.completeStep(currentWorkspaceId, "customize_branding");
			}
		}
	}, [brandKit, currentWorkspaceId]);

	// Manual save function - saves all changes at once
	const handleSave = useCallback(async () => {
		if (!currentWorkspaceId || !hasUnsavedChanges) return;

		// Clear any existing timeout
		if (savedTimeoutRef.current) {
			clearTimeout(savedTimeoutRef.current);
		}

		setSaveStatus("saving");

		try {
			// Track the final logo URLs after all uploads/deletes
			let finalPrimaryLogo = primaryLogo;
			let finalIconOnlyLogo = iconOnlyLogo;
			let finalLabelOnlyLogo = labelOnlyLogo;
			let finalFaviconLogo = faviconLogo;
			let finalInvertedPrimaryLogo = invertedPrimaryLogo;
			let finalInvertedIconLogo = invertedIconLogo;
			let finalInvertedLabelLogo = invertedLabelLogo;

			// Process pending image changes
			if (pendingImageChanges.primaryLogo) {
				if (pendingImageChanges.primaryLogo.action === "upload" && pendingImageChanges.primaryLogo.dataUrl) {
					const file = dataURLtoFile(pendingImageChanges.primaryLogo.dataUrl, "logo-icon-with-label.jpg");
					const result = await updateIconWithLabel(currentWorkspaceId, file);
					if (result) {
						finalPrimaryLogo = result.logo_icon_with_label_url;
					}
				} else if (pendingImageChanges.primaryLogo.action === "delete") {
					await deleteIconWithLabel(currentWorkspaceId);
					finalPrimaryLogo = null;
				}
			}

			if (pendingImageChanges.iconOnlyLogo) {
				if (pendingImageChanges.iconOnlyLogo.action === "upload" && pendingImageChanges.iconOnlyLogo.dataUrl) {
					const file = dataURLtoFile(pendingImageChanges.iconOnlyLogo.dataUrl, "logo-icon-only.jpg");
					const result = await updateLogoIconOnly(currentWorkspaceId, file);
					if (result) {
						finalIconOnlyLogo = result.logo_icon_only_url;
					}
				} else if (pendingImageChanges.iconOnlyLogo.action === "delete") {
					await deleteLogoIconOnly(currentWorkspaceId);
					finalIconOnlyLogo = null;
				}
			}

			if (pendingImageChanges.labelOnlyLogo) {
				if (pendingImageChanges.labelOnlyLogo.action === "upload" && pendingImageChanges.labelOnlyLogo.dataUrl) {
					const file = dataURLtoFile(pendingImageChanges.labelOnlyLogo.dataUrl, "logo-label-only.jpg");
					const result = await updateLogoLabelOnly(currentWorkspaceId, file);
					if (result) {
						finalLabelOnlyLogo = result.logo_label_only_url;
					}
				} else if (pendingImageChanges.labelOnlyLogo.action === "delete") {
					await deleteLogoLabelOnly(currentWorkspaceId);
					finalLabelOnlyLogo = null;
				}
			}

			if (pendingImageChanges.faviconLogo) {
				if (pendingImageChanges.faviconLogo.action === "upload" && pendingImageChanges.faviconLogo.dataUrl) {
					const file = dataURLtoFile(pendingImageChanges.faviconLogo.dataUrl, "favicon.png");
					const result = await updateFavicon(currentWorkspaceId, file);
					if (result) {
						finalFaviconLogo = result.favicon_url;
					}
				} else if (pendingImageChanges.faviconLogo.action === "delete") {
					await deleteFavicon(currentWorkspaceId);
					finalFaviconLogo = null;
				}
			}

			if (pendingImageChanges.invertedPrimaryLogo) {
				if (pendingImageChanges.invertedPrimaryLogo.action === "upload" && pendingImageChanges.invertedPrimaryLogo.dataUrl) {
					const file = dataURLtoFile(pendingImageChanges.invertedPrimaryLogo.dataUrl, "logo-inverted-icon-with-label.jpg");
					const result = await updateLogoInvertedIconWithLabel(currentWorkspaceId, file);
					if (result) {
						finalInvertedPrimaryLogo = result.logo_inverted_icon_with_label_url;
					}
				} else if (pendingImageChanges.invertedPrimaryLogo.action === "delete") {
					await deleteLogoInvertedIconWithLabel(currentWorkspaceId);
					finalInvertedPrimaryLogo = null;
				}
			}

			if (pendingImageChanges.invertedIconLogo) {
				if (pendingImageChanges.invertedIconLogo.action === "upload" && pendingImageChanges.invertedIconLogo.dataUrl) {
					const file = dataURLtoFile(pendingImageChanges.invertedIconLogo.dataUrl, "logo-inverted-icon-only.jpg");
					const result = await updateLogoInvertedIconOnly(currentWorkspaceId, file);
					if (result) {
						finalInvertedIconLogo = result.logo_inverted_icon_only_url;
					}
				} else if (pendingImageChanges.invertedIconLogo.action === "delete") {
					await deleteLogoInvertedIconOnly(currentWorkspaceId);
					finalInvertedIconLogo = null;
				}
			}

			if (pendingImageChanges.invertedLabelLogo) {
				if (pendingImageChanges.invertedLabelLogo.action === "upload" && pendingImageChanges.invertedLabelLogo.dataUrl) {
					const file = dataURLtoFile(pendingImageChanges.invertedLabelLogo.dataUrl, "logo-inverted-label-only.jpg");
					const result = await updateLogoInvertedLabelOnly(currentWorkspaceId, file);
					if (result) {
						finalInvertedLabelLogo = result.logo_inverted_label_only_url;
					}
				} else if (pendingImageChanges.invertedLabelLogo.action === "delete") {
					await deleteLogoInvertedLabelOnly(currentWorkspaceId);
					finalInvertedLabelLogo = null;
				}
			}

			// Update brand kit with colors and fonts
			const updateData: BrandKitUpdateRequest = {
				color_primary_hex: primaryColor.hex,
				color_accent_hex: accentColor.hex,
				font_heading_name: headingFont,
				font_heading_weight: headingWeight,
				font_body_name: bodyFont,
				font_body_weight: bodyWeight,
			};

			await updateBrandKit(currentWorkspaceId, updateData);

			// Update local logo states with final URLs
			setPrimaryLogo(finalPrimaryLogo);
			setIconOnlyLogo(finalIconOnlyLogo);
			setLabelOnlyLogo(finalLabelOnlyLogo);
			setFaviconLogo(finalFaviconLogo);
			setInvertedPrimaryLogo(finalInvertedPrimaryLogo);
			setInvertedIconLogo(finalInvertedIconLogo);
			setInvertedLabelLogo(finalInvertedLabelLogo);

			// Update original values after successful save
			setOriginalValues({
				primaryColor: normalizeHex(primaryColor.hex, "#000000"),
				accentColor: normalizeHex(accentColor.hex, "#777777"),
				headingFont,
				headingWeight,
				bodyFont,
				bodyWeight,
				primaryLogo: finalPrimaryLogo,
				iconOnlyLogo: finalIconOnlyLogo,
				labelOnlyLogo: finalLabelOnlyLogo,
				faviconLogo: finalFaviconLogo,
				invertedPrimaryLogo: finalInvertedPrimaryLogo,
				invertedIconLogo: finalInvertedIconLogo,
				invertedLabelLogo: finalInvertedLabelLogo,
			});

			// Clear pending changes
			setPendingImageChanges({});

			// Mark onboarding step complete
			onboardingStore.completeStep(currentWorkspaceId, "customize_branding");

			setSaveStatus("saved");

			// Hide "Saved" after 2 seconds
			savedTimeoutRef.current = setTimeout(() => {
				setSaveStatus("idle");
			}, 2000);
		} catch (error) {
			console.error("Failed to save brand kit:", error);
			setSaveStatus("error");

			// Reset error status after 3 seconds
			savedTimeoutRef.current = setTimeout(() => {
				setSaveStatus("idle");
			}, 3000);
		}
	}, [
		currentWorkspaceId,
		hasUnsavedChanges,
		primaryColor.hex,
		accentColor.hex,
		headingFont,
		headingWeight,
		bodyFont,
		bodyWeight,
		pendingImageChanges,
		primaryLogo,
		iconOnlyLogo,
		labelOnlyLogo,
		faviconLogo,
		invertedPrimaryLogo,
		invertedIconLogo,
		invertedLabelLogo,
		updateBrandKit,
		updateIconWithLabel,
		deleteIconWithLabel,
		updateLogoIconOnly,
		deleteLogoIconOnly,
		updateLogoLabelOnly,
		deleteLogoLabelOnly,
		updateFavicon,
		deleteFavicon,
		updateLogoInvertedIconWithLabel,
		deleteLogoInvertedIconWithLabel,
		updateLogoInvertedIconOnly,
		deleteLogoInvertedIconOnly,
		updateLogoInvertedLabelOnly,
		deleteLogoInvertedLabelOnly,
	]);

	// Discard function - resets to original values
	const handleDiscard = useCallback(() => {
		setPrimaryColor(ColorService.convert("hex", originalValues.primaryColor));
		setAccentColor(ColorService.convert("hex", originalValues.accentColor));
		setHeadingFont(originalValues.headingFont);
		setHeadingWeight(originalValues.headingWeight);
		setBodyFont(originalValues.bodyFont);
		setBodyWeight(originalValues.bodyWeight);

		// Reset logo states to original values
		setPrimaryLogo(originalValues.primaryLogo);
		setIconOnlyLogo(originalValues.iconOnlyLogo);
		setLabelOnlyLogo(originalValues.labelOnlyLogo);
		setFaviconLogo(originalValues.faviconLogo);
		setInvertedPrimaryLogo(originalValues.invertedPrimaryLogo);
		setInvertedIconLogo(originalValues.invertedIconLogo);
		setInvertedLabelLogo(originalValues.invertedLabelLogo);

		// Clear pending image changes
		setPendingImageChanges({});
	}, [originalValues, setPrimaryColor, setAccentColor]);

	// Warn user before leaving page with unsaved changes (browser navigation)
	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (hasUnsavedChanges) {
				e.preventDefault();
				// Modern browsers require returnValue to be set
				e.returnValue = "";
				return "";
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [hasUnsavedChanges]);

	// Block browser back/forward navigation when there are unsaved changes
	useEffect(() => {
		if (!hasUnsavedChanges) return;

		// Push a dummy state to enable popstate detection
		window.history.pushState(null, "", window.location.href);

		const handlePopState = () => {
			if (hasUnsavedChanges) {
				// Show confirmation dialog
				const confirmLeave = window.confirm("You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.");

				if (confirmLeave) {
					// Allow navigation by going back again
					window.history.back();
				} else {
					// Stay on page - push state again to re-enable detection
					window.history.pushState(null, "", window.location.href);
				}
			}
		};

		window.addEventListener("popstate", handlePopState);
		return () => {
			window.removeEventListener("popstate", handlePopState);
		};
	}, [hasUnsavedChanges]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (savedTimeoutRef.current) {
				clearTimeout(savedTimeoutRef.current);
			}
		};
	}, []);

	// Color change handlers - now local only, no autosave
	const handlePrimaryColorChange = (color: IColor) => {
		setPrimaryColor(color);
	};

	const handlePrimaryColorComplete = useCallback((_color: IColor) => {
		// No-op: color is already updated via handlePrimaryColorChange
		// Save will be done via manual save button
	}, []);

	const handleAccentColorChange = (color: IColor) => {
		setAccentColor(color);
	};

	const handleAccentColorComplete = useCallback((_color: IColor) => {
		// No-op: color is already updated via handleAccentColorChange
		// Save will be done via manual save button
	}, []);

	// Font change handlers - now local only, no autosave
	const handleHeadingFontChange = useCallback((value: string) => {
		setHeadingFont(value);
	}, []);

	const handleHeadingWeightChange = useCallback((value: string) => {
		setHeadingWeight(value);
	}, []);

	const handleBodyFontChange = useCallback((value: string) => {
		setBodyFont(value);
	}, []);

	const handleBodyWeightChange = useCallback((value: string) => {
		setBodyWeight(value);
	}, []);

	// Preset selection handler
	const handlePresetSelect = useCallback(
		(preset: (typeof BRANDING_PRESETS)[0]) => {
			if (hasCustomBranding) {
				setPendingPreset(preset);
				setShowPresetConfirm(true);
			} else {
				setPrimaryColor(ColorService.convert("hex", preset.primaryColor));
				setAccentColor(ColorService.convert("hex", preset.accentColor));
				setHeadingFont(preset.headingFont);
				setHeadingWeight(preset.headingWeight);
				setBodyFont(preset.bodyFont);
				setBodyWeight(preset.bodyWeight);
			}
		},
		[hasCustomBranding]
	);

	// Confirm preset selection (replace custom branding)
	const handleConfirmPreset = useCallback(() => {
		if (pendingPreset) {
			setPrimaryColor(ColorService.convert("hex", pendingPreset.primaryColor));
			setAccentColor(ColorService.convert("hex", pendingPreset.accentColor));
			setHeadingFont(pendingPreset.headingFont);
			setHeadingWeight(pendingPreset.headingWeight);
			setBodyFont(pendingPreset.bodyFont);
			setBodyWeight(pendingPreset.bodyWeight);
		}
		setShowPresetConfirm(false);
		setPendingPreset(null);
	}, [pendingPreset]);

	// Cancel preset selection
	const handleCancelPreset = useCallback(() => {
		setShowPresetConfirm(false);
		setPendingPreset(null);
	}, []);

	// Upgrade click handler
	const handleUpgradeClick = useCallback(() => {
		openUpgradeModal({
			context: "branding_customization",
			workspaceId: currentWorkspaceId ?? undefined,
			userRole: currentBrand?.role,
		});
	}, [openUpgradeModal, currentWorkspaceId, currentBrand?.role]);

	// Logo change handlers - now store data URLs locally, upload on save
	const handlePrimaryLogoChange = useCallback((dataUrl: string | null) => {
		if (dataUrl) {
			// Store data URL for preview and mark for upload on save
			setPrimaryLogo(dataUrl);
			setPendingImageChanges((prev) => ({
				...prev,
				primaryLogo: { dataUrl, action: "upload" },
			}));
		} else {
			// Mark for deletion on save
			setPrimaryLogo(null);
			setPendingImageChanges((prev) => ({
				...prev,
				primaryLogo: { dataUrl: null, action: "delete" },
			}));
		}
	}, []);

	const handleIconOnlyLogoChange = useCallback((dataUrl: string | null) => {
		if (dataUrl) {
			setIconOnlyLogo(dataUrl);
			setPendingImageChanges((prev) => ({
				...prev,
				iconOnlyLogo: { dataUrl, action: "upload" },
			}));
		} else {
			setIconOnlyLogo(null);
			setPendingImageChanges((prev) => ({
				...prev,
				iconOnlyLogo: { dataUrl: null, action: "delete" },
			}));
		}
	}, []);

	const handleLabelOnlyLogoChange = useCallback((dataUrl: string | null) => {
		if (dataUrl) {
			setLabelOnlyLogo(dataUrl);
			setPendingImageChanges((prev) => ({
				...prev,
				labelOnlyLogo: { dataUrl, action: "upload" },
			}));
		} else {
			setLabelOnlyLogo(null);
			setPendingImageChanges((prev) => ({
				...prev,
				labelOnlyLogo: { dataUrl: null, action: "delete" },
			}));
		}
	}, []);

	const handleFaviconLogoChange = useCallback((dataUrl: string | null) => {
		if (dataUrl) {
			setFaviconLogo(dataUrl);
			setPendingImageChanges((prev) => ({
				...prev,
				faviconLogo: { dataUrl, action: "upload" },
			}));
		} else {
			setFaviconLogo(null);
			setPendingImageChanges((prev) => ({
				...prev,
				faviconLogo: { dataUrl: null, action: "delete" },
			}));
		}
	}, []);

	const handleInvertedPrimaryLogoChange = useCallback((dataUrl: string | null) => {
		if (dataUrl) {
			setInvertedPrimaryLogo(dataUrl);
			setPendingImageChanges((prev) => ({
				...prev,
				invertedPrimaryLogo: { dataUrl, action: "upload" },
			}));
		} else {
			setInvertedPrimaryLogo(null);
			setPendingImageChanges((prev) => ({
				...prev,
				invertedPrimaryLogo: { dataUrl: null, action: "delete" },
			}));
		}
	}, []);

	const handleInvertedIconLogoChange = useCallback((dataUrl: string | null) => {
		if (dataUrl) {
			setInvertedIconLogo(dataUrl);
			setPendingImageChanges((prev) => ({
				...prev,
				invertedIconLogo: { dataUrl, action: "upload" },
			}));
		} else {
			setInvertedIconLogo(null);
			setPendingImageChanges((prev) => ({
				...prev,
				invertedIconLogo: { dataUrl: null, action: "delete" },
			}));
		}
	}, []);

	const handleInvertedLabelLogoChange = useCallback((dataUrl: string | null) => {
		if (dataUrl) {
			setInvertedLabelLogo(dataUrl);
			setPendingImageChanges((prev) => ({
				...prev,
				invertedLabelLogo: { dataUrl, action: "upload" },
			}));
		} else {
			setInvertedLabelLogo(null);
			setPendingImageChanges((prev) => ({
				...prev,
				invertedLabelLogo: { dataUrl: null, action: "delete" },
			}));
		}
	}, []);

	// Load Google Fonts on demand when font selection changes
	useEffect(() => {
		if (headingFont && headingWeight) {
			loadGoogleFont(headingFont, [headingWeight]);
		}
	}, [headingFont, headingWeight]);

	useEffect(() => {
		if (bodyFont && bodyWeight) {
			loadGoogleFont(bodyFont, [bodyWeight]);
		}
	}, [bodyFont, bodyWeight]);

	return (
		<div className="relative py-12">
			<Container className="pb-16">
				<div className="mb-8">
					<h1 className="text-xl font-semibold text-primary">Branding</h1>
					<p className="mt-1 text-sm text-tertiary">Customize your workspace's visual identity</p>
				</div>
				{isLoading ? (
					<BrandingSkeleton />
				) : (
					<div className="flex border border-gray-200 bg-white rounded-xl">
						{/* Left Panel - Settings */}
						<div
							className={`w-85 border-r transition-colors duration-200 rounded-tl-xl rounded-bl-xl ${
								logoImageMode === "dark" ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"
							}`}
						>
							{/* Presets Section - Only shown when branding_customization is false */}
							{!canCustomizeBranding && (
								<div className={`p-6 border-b transition-colors ${logoImageMode === "dark" ? "border-gray-800" : "border-gray-100"}`}>
									<div className="flex items-center gap-2.5 mb-6">
										<svg
											className={`w-5 h-5 transition-colors ${logoImageMode === "dark" ? "text-gray-500" : "text-gray-400"}`}
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth={1.5}
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M9.53 17.568a1 1 0 01-.447-.283l-2.5-2.5a1 1 0 011.414-1.414l1.879 1.879 4.38-5.88a1 1 0 111.624 1.208l-5 6.7a1 1 0 01-.76.39z"
											/>
											<path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
										</svg>
										<h2 className={`font-semibold transition-colors ${logoImageMode === "dark" ? "text-white" : "text-gray-900"}`}>Presets</h2>
									</div>
									<div className="grid grid-cols-2 gap-3">
										{BRANDING_PRESETS.map((preset) => (
											<button
												key={preset.id}
												type="button"
												onClick={() => handlePresetSelect(preset)}
												className={`p-3 rounded-xl border-2 transition-all ${
													selectedPresetId === preset.id
														? "border-primary bg-primary/5"
														: logoImageMode === "dark"
															? "border-gray-700 hover:border-gray-600 hover:bg-gray-800"
															: "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
												}`}
											>
												<div className="flex gap-1.5 mb-2 justify-center">
													<div
														className="w-6 h-6 rounded-full border border-gray-200"
														style={{ backgroundColor: preset.primaryColor }}
													/>
													<div
														className="w-6 h-6 rounded-full border border-gray-200"
														style={{ backgroundColor: preset.accentColor }}
													/>
												</div>
												<p className={`text-sm font-medium ${logoImageMode === "dark" ? "text-gray-200" : "text-gray-900"}`}>{preset.name}</p>
											</button>
										))}
									</div>

									{/* Upgrade panel */}
									<div className={`mt-6 p-4 rounded-xl border ${logoImageMode === "dark" ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
										<div className="flex items-start gap-3">
											<div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${logoImageMode === "dark" ? "bg-blue-900/50" : "bg-blue-100"}`}>
												<svg className={`w-4 h-4 ${logoImageMode === "dark" ? "text-blue-400" : "text-blue-600"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
													<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
												</svg>
											</div>
											<div className="flex-1">
												<p className={`text-sm font-medium ${logoImageMode === "dark" ? "text-gray-200" : "text-gray-900"}`}>Want more customization?</p>
												<p className={`text-xs mt-0.5 ${logoImageMode === "dark" ? "text-gray-400" : "text-gray-500"}`}>Upgrade to customize colors and fonts to match your brand.</p>
												<Button variant="brand" className="mt-2 text-xs px-3 py-1.5" onClick={handleUpgradeClick}>
													Upgrade
												</Button>
											</div>
										</div>
									</div>
								</div>
							)}

							{/* Colors Section */}
							{canCustomizeBranding && (
								<div className={`p-6 border-b transition-colors ${logoImageMode === "dark" ? "border-gray-800" : "border-gray-100"}`}>
								<div className="flex items-center gap-2.5 mb-6">
									<svg
										className={`w-5 h-5 transition-colors ${logoImageMode === "dark" ? "text-gray-500" : "text-gray-400"}`}
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
										strokeWidth={1.5}
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
										/>
									</svg>
									<h2 className={`font-semibold transition-colors ${logoImageMode === "dark" ? "text-white" : "text-gray-900"}`}>Colors</h2>
								</div>
								<div className="space-y-5">
									<ColorPickerDropdown
										color={primaryColor}
										onChange={handlePrimaryColorChange}
										onChangeComplete={handlePrimaryColorComplete}
										label="Primary Color"
										onValidationChange={setPrimaryColorError}
										defaultColor="#432dd7"
										variant={logoImageMode}
									/>
									<ColorPickerDropdown
										color={accentColor}
										onChange={handleAccentColorChange}
										onChangeComplete={handleAccentColorComplete}
										label="Accent Color"
										onValidationChange={setAccentColorError}
										defaultColor="#e17100"
										variant={logoImageMode}
									/>
								</div>
							</div>
							)}

							{/* Typography Section */}
							{canCustomizeBranding && (
								<div className={`p-6 border-b transition-colors ${logoImageMode === "dark" ? "border-gray-800" : "border-gray-100"}`}>
								<div className="flex items-center gap-2.5 mb-6">
									<svg
										className={`w-5 h-5 transition-colors ${logoImageMode === "dark" ? "text-gray-500" : "text-gray-400"}`}
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
										strokeWidth={1.5}
									>
										<path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16m-7 6h7" />
									</svg>
									<h2 className={`font-semibold transition-colors ${logoImageMode === "dark" ? "text-white" : "text-gray-900"}`}>Typography</h2>
								</div>
								<div className="space-y-5">
									{/* Heading Font */}
									<div>
										<label className={`block font-medium text-sm mb-2 transition-colors ${logoImageMode === "dark" ? "text-gray-300" : "text-gray-700"}`}>Heading Font</label>
										<div className="space-y-2">
											<FontSelect
												options={headingFontOptions}
												value={headingFont}
												onChange={handleHeadingFontChange}
												placeholder="Select font family..."
												variant={logoImageMode}
											/>
											<Select options={headingWeightOptions} value={headingWeight} onChange={handleHeadingWeightChange} placeholder="Select weight..." variant={logoImageMode} />
										</div>
									</div>

									{/* Body Font */}
									<div>
										<label className={`block font-medium text-sm mb-2 transition-colors ${logoImageMode === "dark" ? "text-gray-300" : "text-gray-700"}`}>Body Font</label>
										<div className="space-y-2">
											<FontSelect options={bodyFontOptions} value={bodyFont} onChange={handleBodyFontChange} placeholder="Select font family..." variant={logoImageMode} />
											<Select options={bodyWeightOptions} value={bodyWeight} onChange={handleBodyWeightChange} placeholder="Select weight..." variant={logoImageMode} />
										</div>
									</div>
								</div>
							</div>
							)}

							{/* Logo Section */}
							<div className="p-6">
								<div className="flex items-center gap-2.5 mb-6">
									<svg
										className={`w-5 h-5 transition-colors ${logoImageMode === "dark" ? "text-gray-500" : "text-gray-400"}`}
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
										strokeWidth={1.5}
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
										/>
									</svg>
									<h2 className={`font-semibold transition-colors ${logoImageMode === "dark" ? "text-white" : "text-gray-900"}`}>Logo</h2>
								</div>

								{logoImageMode === "light" && (
									<div className="space-y-6">
										<div>
											<div className="flex items-center justify-between mb-1">
												<p className="font-medium text-gray-700 text-sm">Icon with label</p>
												<span className="text-xs bg-blue-50 text-blue-700 py-1 px-2 rounded-lg">Recommended</span>
											</div>
											<p className="text-xs text-gray-500 mb-3">Full logo with icon and text</p>
											<ImageUploadField
												value={primaryLogo}
												onChange={handlePrimaryLogoChange}
												accept="image/jpeg,image/png,image/webp"
												allowedTypes={["image/jpeg", "image/png", "image/webp"]}
											/>
										</div>
										<div>
											<p className="font-medium text-gray-700 text-sm mb-1">Icon only</p>
											<p className="text-xs text-gray-500 mb-3">Square logo icon without text</p>
											<div className="w-20">
												<ImageUploadField
													value={iconOnlyLogo}
													onChange={handleIconOnlyLogoChange}
													aspectRatio={1}
													accept="image/jpeg,image/png,image/webp"
													allowedTypes={["image/jpeg", "image/png", "image/webp"]}
												/>
											</div>
										</div>
										<div>
											<p className="font-medium text-gray-700 text-sm mb-1">Label only</p>
											<p className="text-xs text-gray-500 mb-3">Text-only wordmark</p>
											<ImageUploadField
												value={labelOnlyLogo}
												onChange={handleLabelOnlyLogoChange}
												accept="image/jpeg,image/png,image/webp"
												allowedTypes={["image/jpeg", "image/png", "image/webp"]}
											/>
										</div>
										<div>
											<p className="font-medium text-gray-700 text-sm mb-1">Favicon</p>
											<p className="text-xs text-gray-500 mb-3">Browser tab icon (square)</p>
											<div className="w-20">
												<ImageUploadField
													value={faviconLogo}
													onChange={handleFaviconLogoChange}
													aspectRatio={1}
													outputSize={32}
													maxDimension={32}
													accept="image/png"
													allowedTypes={["image/png"]}
												/>
											</div>
										</div>
									</div>
								)}
								{logoImageMode === "dark" && (
									<div className="space-y-6">
										<div>
											<div className="flex items-center justify-between mb-1">
												<p className="font-medium text-gray-300 text-sm">Icon with label</p>
												<span className="text-xs text-gray-500">For dark backgrounds</span>
											</div>
											<p className="text-xs text-gray-500 mb-3">Inverted colors for dark themes</p>
											<ImageUploadField value={invertedPrimaryLogo} onChange={handleInvertedPrimaryLogoChange} variant="dark" />
										</div>
										<div>
											<p className="font-medium text-gray-300 text-sm mb-1">Icon only</p>
											<p className="text-xs text-gray-500 mb-3">Light icon for dark backgrounds</p>
											<div className="w-20">
												<ImageUploadField value={invertedIconLogo} onChange={handleInvertedIconLogoChange} variant="dark" aspectRatio={1} />
											</div>
										</div>
										<div>
											<p className="font-medium text-gray-300 text-sm mb-1">Label only</p>
											<p className="text-xs text-gray-500 mb-3">Light wordmark for dark backgrounds</p>
											<ImageUploadField value={invertedLabelLogo} onChange={handleInvertedLabelLogoChange} variant="dark" />
										</div>
									</div>
								)}
							</div>
						</div>
						{/* Right Panel - Preview */}
						<div className="flex-1 flex flex-col rounded-tr-xl rounded-br-xl">
							<div
								className={`flex items-center justify-between px-4 py-3 border-b rounded-tr-xl transition-colors ${
									previewDarkMode ? "bg-gray-900 border-gray-800" : "bg-white border-gray-100"
								}`}
							>
								<div className="flex items-center gap-6">
									<h2 className={`font-semibold text-sm transition-colors ${previewDarkMode ? "text-white" : "text-gray-900"}`}>Brand Preview</h2>
									<span className={`text-xs transition-colors ${previewDarkMode ? "text-gray-500" : "text-gray-400"}`}>Live preview of your brand settings</span>
								</div>
							</div>

							<div className={`flex flex-col flex-1 rounded-br-xl ${previewDarkMode ? "bg-gray-950" : "bg-gray-50"}`}>
								{/* Progress bar or accent bar at top - sticky */}
								<div className="z-10 h-1 w-full shrink-0" style={{ backgroundColor: primaryColor.hex }} />

								{/* Main container - centered with max width */}
								<div className="flex-1 flex flex-col w-full max-w-xl mx-auto px-6 md:px-8">
									{/* Header with logo */}
									<header className="pt-20 pb-10 shrink-0">
										{previewDarkMode ? (
											invertedPrimaryLogo ? (
												<img src={invertedPrimaryLogo} alt={brandName} title={brandName} className="h-8 md:h-10 w-auto object-contain" />
											) : invertedIconLogo ? (
												<img src={invertedIconLogo} alt={brandName} title={brandName} className="h-8 md:h-10 w-auto object-contain" />
											) : invertedLabelLogo ? (
												<img src={invertedLabelLogo} alt={brandName} title={brandName} className="h-8 md:h-10 w-auto object-contain" />
											) : (
												<span className="text-xl font-bold" style={{ color: "#ffffff", fontFamily: bodyFont, fontWeight: bodyWeight }}>
													{brandName}
												</span>
											)
										) : primaryLogo ? (
											<img src={primaryLogo} alt={brandName} title={brandName} className="h-8 md:h-10 w-auto object-contain" />
										) : iconOnlyLogo ? (
											<img src={iconOnlyLogo} alt={brandName} title={brandName} className="h-8 md:h-10 w-auto object-contain" />
										) : labelOnlyLogo ? (
											<img src={labelOnlyLogo} alt={brandName} title={brandName} className="h-8 md:h-10 w-auto object-contain" />
										) : (
											<span className={`text-xl font-bold ${previewDarkMode ? "text-white" : "text-primary"}`} style={{ fontFamily: bodyFont, fontWeight: bodyWeight }}>
												{brandName}
											</span>
										)}
									</header>

									{/* Content area */}
									<main className="flex-1 flex flex-col">
										{/* Title */}
										<div className="animate-fade-in-up">
											<h1
												className={`text-2xl md:text-3xl lg:text-4xl leading-[1.3] tracking-tight ${previewDarkMode ? "text-white" : "text-primary"}`}
												style={{
													fontFamily: `"${headingFont}", sans-serif`,
													fontWeight: headingWeight,
												}}
											>
												Sample Title
											</h1>
										</div>

										{/* Subtitle */}
										<div className="animate-fade-in-up animation-delay-100">
											<p
												className={`mt-3 text-base md:text-lg leading-relaxed ${previewDarkMode ? "text-gray-300" : "text-secondary"}`}
												style={{
													fontFamily: `"${bodyFont}", sans-serif`,
													fontWeight: bodyWeight,
												}}
											>
												This is how your brand will appear across all your testimonial collection forms.
											</p>
										</div>

										{/* Main content */}
										<div className="mt-8 md:mt-10 animate-fade-in-up animation-delay-200">
											<div className={`p-4 rounded-lg border ${previewDarkMode ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"}`}>
												<p
													className={previewDarkMode ? "text-gray-300" : "text-gray-700"}
													style={{
														fontFamily: `"${bodyFont}", sans-serif`,
														fontWeight: bodyWeight,
													}}
												>
													Content goes here.
												</p>
											</div>
										</div>
									</main>
								</div>

								{/* Footer with buttons - sticky, full width */}
								<footer className="sticky bottom-16 z-10 shrink-0 overflow-hidden">
									{/* Fade gradient above toolbar */}
									<div className={`h-16 pointer-events-none ${previewDarkMode ? "bg-linear-to-t from-gray-950 to-transparent" : "bg-linear-to-t from-gray-50 to-transparent"}`} />
									<div className={`py-5 md:py-6 border-t rounded-br-xl ${previewDarkMode ? "bg-gray-900 border-gray-800" : "bg-white border-gray-100"}`}>
										<div className="w-full max-w-xl mx-auto px-6 md:px-8">
											<div className="flex items-center gap-3">
												{/* Back button - outlined style */}
												<div className="w-30 shrink-0">
													<button
														type="button"
														className={`btn-3d w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-100 whitespace-nowrap ${
															previewDarkMode
																? "text-white bg-gray-800 border border-gray-700 hover:bg-gray-700 shadow-[0_3px_0_0_#374151]"
																: "text-primary bg-white border border-gray-300 hover:bg-gray-50 shadow-[0_3px_0_0_#d1d5db]"
														}`}
														style={{
															fontFamily: `"${bodyFont}", sans-serif`,
															fontWeight: bodyWeight,
														}}
													>
														<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
															<path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
														</svg>
														Back
													</button>
												</div>

												{/* Center branding - nudge down for optical vertical centering */}
												<div className="flex-1 flex justify-center translate-y-0.5">
													{selectedBrand?.branding !== "disabled" && (
														<div className="badge-gradient-silver rounded-full p-[1.5px]">
															{/* <span className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${previewDarkMode ? "text-gray-400" : "text-gray-500"}`}>
																Powered by{" "}
																<a href="https://sayabout.us/" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">
																	Say About Us
																</a>
															</span> */}

															<a href="https://sayabout.us/" target="_blank" rel="noopener noreferrer" className="underline block hover:opacity-80">
																<img src="/images/badge-sayaboutus-2.svg" alt="build social proof with Say About Us" className="h-7 w-auto" />
															</a>
														</div>
													)}
												</div>

												{/* Continue button - filled style */}
												<div className="w-30 shrink-0">
													<button
														type="button"
														className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-100 whitespace-nowrap btn-3d"
														style={{
															backgroundColor: accentColor.hex,
															boxShadow: `0 3px 0 0 color-mix(in srgb, ${accentColor.hex} 70%, black)`,
															color: getTextColorForBackground(accentColor.hex),
															fontFamily: `"${bodyFont}", sans-serif`,
															fontWeight: bodyWeight,
														}}
													>
														Continue
														<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
															<path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
														</svg>
													</button>
												</div>
											</div>
										</div>
									</div>
								</footer>
							</div>
						</div>
					</div>
				)}
			</Container>

			{/* Sticky footer with Save/Discard buttons */}
			<div
				className={`fixed bottom-0 left-0 z-20 bg-white border-t border-gray-200 px-8 py-4 transition-all duration-300 ${isSidebarCollapsed ? "w-[calc(100%-64px)] ml-16" : "w-[calc(100%-256px)] ml-64"}`}
			>
				<Container>
					<div className="flex items-center justify-end">
						<div className="flex items-center gap-3">
							{/* Status indicator */}
							{!isLoading && saveStatus !== "idle" && (
								<span
									className={`text-sm font-medium flex items-center gap-1.5 transition-opacity duration-200 ${
										saveStatus === "saving" ? "text-gray-500" : saveStatus === "saved" ? "text-green-600" : "text-red-600"
									}`}
								>
									{saveStatus === "saving" ? (
										<>
											<svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
												<path
													className="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
												/>
											</svg>
											Saving...
										</>
									) : saveStatus === "saved" ? (
										<>
											<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
											</svg>
											Saved
										</>
									) : (
										<>
											<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
											</svg>
											Error saving
										</>
									)}
								</span>
							)}

							{/* Discard button */}
							<Button
								variant="light"
								onClick={handleDiscard}
								disabled={!hasUnsavedChanges || saveStatus === "saving" || saveStatus === "saved" || !!primaryColorError || !!accentColorError}
							>
								Discard
							</Button>

							{/* Save button */}
							<Button
								variant="default"
								onClick={handleSave}
								disabled={!hasUnsavedChanges || saveStatus === "saving" || saveStatus === "saved" || !!primaryColorError || !!accentColorError}
							>
								{saveStatus === "saving" ? "Saving..." : "Save changes"}
							</Button>
						</div>
					</div>
				</Container>
			</div>

			{/* Preset Selection Confirmation Modal */}
			<Dialog.Root open={showPresetConfirm} onOpenChange={setShowPresetConfirm}>
				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 modal-overlay" />
					<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-lg modal-content overflow-hidden">
						<div className="flex items-center justify-center w-14 h-14 mx-auto mt-6 mb-4 rounded-full bg-amber-100">
							<svg className="w-7 h-7 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
							</svg>
						</div>

						<Dialog.Title className="text-lg font-semibold text-gray-900 text-center mb-2">Replace Branding?</Dialog.Title>

						<Dialog.Description className="sr-only">
							Switching to this preset will replace your current custom branding.
						</Dialog.Description>
						<div className="text-sm text-gray-600 text-center mb-6 px-6">
							{currentBrand?.role === "admin" || currentBrand?.has_billing_access ? (
								<div className="space-y-2">
									<p>Switching to this preset will replace your current custom branding.</p>
									<p>Colors and typography will be updated, while your logo will remain unchanged.</p>
									<p>You won't be able to recreate or restore this setup on this plan once it's changed. Upgrade to access custom branding again.</p>
								</div>
							) : (
								<div className="space-y-2">
									<p>Switching to this preset will replace the current custom branding.</p>
									<p>Colors and typography will be updated, while the logo will remain unchanged.</p>
									<p>This setup cannot be recreated or restored on this plan once changed. Please contact your workspace admin for custom branding access.</p>
								</div>
							)}
						</div>

						<div className="px-6 pb-6 space-y-3">
							<Button variant="default" className="w-full" onClick={handleConfirmPreset}>
								Apply Preset
							</Button>
							<Button variant="light" className="w-full" onClick={handleCancelPreset}>
								Cancel
							</Button>
						</div>

						<Dialog.Close asChild>
							<button className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
								<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
									<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</Dialog.Close>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>
		</div>
	);
}
